import { useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Spinner } from '@/components/ui/Spinner'
import { Textarea } from '@/components/ui/Textarea'
import { canWriteDepartment } from '@/auth/profile'
import { useProfile } from '@/auth/SessionProvider'
import { listDepartmentMembers } from '@/data/members'
import { importMembers } from '@/data/memberImport'
import type { ImportOutcome } from '@/data/memberImport'
import { downloadCsv } from '@/lib/csv'
import { formatDay } from '@/lib/format'
import { IMPORT_TEMPLATE, MAX_IMPORT_ROWS, planImport } from '@/lib/memberImport'
import type { ImportPlan, ImportRow } from '@/lib/memberImport'
import { formatZambianPhone } from '@/lib/phone'
import { lusakaToday } from '@/lib/periods'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')
const PREVIEW_LIMIT = 50

export function ImportMembersPage({ deptId }: { deptId: string }) {
  const profile = useProfile()
  const canWrite = canWriteDepartment(profile, deptId)
  const qc = useQueryClient()
  const membersQ = useQuery({ queryKey: ['members', deptId], queryFn: () => listDepartmentMembers(deptId) })

  const [text, setText] = useState('')
  const [plan, setPlan] = useState<ImportPlan | null>(null)
  const [fileNote, setFileNote] = useState('')
  const [readError, setReadError] = useState('')
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [outcomes, setOutcomes] = useState<ImportOutcome[] | null>(null)
  const [runError, setRunError] = useState('')
  const busy = useRef(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const readyRows: ImportRow[] = useMemo(() => (plan?.verdicts ?? []).flatMap((v) => (v.kind === 'ready' ? [v.row] : [])), [plan])

  const chooseFile = async (files: FileList | null) => {
    const f = files?.[0]
    if (!f) return
    setReadError('')
    if (/\.xlsx?$/i.test(f.name)) {
      setReadError('That is an Excel file. In Excel choose File > Save As > CSV, then choose the CSV file. Or select the cells in Excel, copy them, and paste them in the box below.')
    } else {
      try {
        setText(await f.text())
        setFileNote(`Loaded ${f.name}`)
        setPlan(null)
        setOutcomes(null)
      } catch {
        setReadError('Could not read that file. Try copying the cells and pasting them in the box instead.')
      }
    }
    if (fileInput.current) fileInput.current.value = ''
  }

  const check = () => {
    setOutcomes(null)
    setRunError('')
    setPlan(planImport(text, membersQ.data ?? [], lusakaToday()))
  }

  const run = async () => {
    if (busy.current || readyRows.length === 0) return
    busy.current = true
    setRunError('')
    setProgress({ done: 0, total: readyRows.length })
    try {
      const out = await importMembers(deptId, readyRows, (done, total) => setProgress({ done, total }))
      setOutcomes(out)
      await qc.invalidateQueries({ queryKey: ['members', deptId] })
    } catch (e) {
      setRunError(errText(e))
    } finally {
      busy.current = false
      setProgress(null)
    }
  }

  const startOver = () => {
    setText(''); setPlan(null); setOutcomes(null); setFileNote(''); setReadError(''); setRunError('')
  }

  if (!canWrite) {
    return (
      <div className="flex flex-col gap-4">
        <h2 className="text-title font-semibold text-ink">Import members</h2>
        <p className="text-muted">You can look at this department but not change it.</p>
        <Link to="/d/$deptId/members" params={{ deptId }} className="text-brand hover:underline">Back to members</Link>
      </div>
    )
  }

  const count = (r: ImportOutcome['result']) => (outcomes ?? []).filter((o) => o.result === r).length
  const notDone = (outcomes ?? []).filter((o) => o.result === 'failed' || o.result === 'not-tried')

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-title font-semibold text-ink">Import members</h2>
        <p className="mt-1 text-muted">
          Add many people at once from a spreadsheet. Nothing is saved until you have seen the list and pressed Import. People already in this department are skipped, and someone who is already in another department is added here without creating a second record.
        </p>
      </div>

      {outcomes ? (
        <Card>
          <CardHeader><h3 className="font-semibold text-ink">Import finished</h3></CardHeader>
          <CardBody>
            <div role="status" className="flex flex-col gap-1 text-ink">
              <p>{count('created')} new {count('created') === 1 ? 'person' : 'people'} added.</p>
              {count('added-existing') > 0 && <p>{count('added-existing')} already in the church records (another department) and now added to yours.</p>}
              {count('already-member') > 0 && <p>{count('already-member')} were already in this department.</p>}
              {notDone.length > 0 && <p className="font-medium text-bad">{notDone.length} could not be added.</p>}
            </div>
            {notDone.length > 0 && (
              <ul className="mt-3 divide-y divide-line rounded-control border border-bad/30 bg-bad-tint px-3" aria-label="People not added">
                {notDone.map((o) => (
                  <li key={o.line} className="py-2 text-body text-bad"><span className="font-medium">Line {o.line}, {o.name}:</span> {o.message}</li>
                ))}
              </ul>
            )}
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to="/d/$deptId/members" params={{ deptId }} className="inline-flex min-h-[44px] items-center rounded-control bg-brand px-4 font-medium text-white">See the members</Link>
              <Button variant="secondary" size="lg" onClick={startOver}>Import another list</Button>
            </div>
          </CardBody>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader><h3 className="font-semibold text-ink">1. Get your list in</h3></CardHeader>
            <CardBody>
              <div className="flex flex-col gap-4">
                <p className="text-body text-muted">
                  Use columns <span className="font-medium">Name, Phone, Email, Date joined, Notes</span>. Only the name is needed. The first row can hold these headings. Up to {MAX_IMPORT_ROWS} people at a time.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <input ref={fileInput} type="file" accept=".csv,.tsv,.txt,text/csv,text/plain" className="sr-only" aria-label="Choose a CSV file" onChange={(e) => void chooseFile(e.target.files)} />
                  <Button variant="secondary" size="lg" onClick={() => fileInput.current?.click()}>Choose a CSV file</Button>
                  <button type="button" className="inline-flex min-h-[44px] items-center rounded-control px-3 text-body font-medium text-brand hover:bg-neutral-tint" onClick={() => downloadCsv('members-template.csv', IMPORT_TEMPLATE)}>
                    Download a blank template
                  </button>
                </div>
                {fileNote && <p role="status" className="text-body text-ok">{fileNote}</p>}
                {readError && <p role="alert" className="text-body text-bad">{readError}</p>}
                <Textarea
                  label="Or paste the rows here (copy the cells in Excel or Google Sheets)"
                  value={text}
                  onChange={(e) => { setText(e.target.value); setPlan(null) }}
                  rows={6}
                  placeholder={'Name\tPhone\nMary Banda\t097 123 4567'}
                />
                <div>
                  <Button size="lg" onClick={check} disabled={text.trim() === '' || membersQ.isPending}>
                    {membersQ.isPending ? 'Loading...' : 'Check the list'}
                  </Button>
                </div>
                {membersQ.isError && <p role="alert" className="text-body text-bad">Could not load the current members to compare with. Try again.</p>}
              </div>
            </CardBody>
          </Card>

          {plan && (
            <Card>
              <CardHeader><h3 className="font-semibold text-ink">2. Check, then import</h3></CardHeader>
              <CardBody>
                {plan.fatal ? (
                  <p role="alert" className="text-bad">{plan.fatal}</p>
                ) : (
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap gap-2" role="status">
                      <Badge tone="success">{plan.ready} ready to add</Badge>
                      {plan.skipped > 0 && <Badge tone="neutral">{plan.skipped} skipped</Badge>}
                      {plan.problems > 0 && <Badge tone="danger">{plan.problems} with a problem</Badge>}
                    </div>

                    {plan.problems > 0 && (
                      <section>
                        <h4 className="text-body font-semibold text-bad">Fix these in your spreadsheet. They will not be imported.</h4>
                        <ul className="mt-1 divide-y divide-bad/20 rounded-control border border-bad/30 bg-bad-tint px-3" aria-label="Rows with a problem">
                          {plan.verdicts.flatMap((v) => (v.kind === 'problem' ? [v] : [])).map((v) => (
                            <li key={v.line} className="py-2 text-body text-bad"><span className="font-medium">Line {v.line}, {v.name}:</span> {v.reasons.join('. ')}</li>
                          ))}
                        </ul>
                      </section>
                    )}

                    {plan.skipped > 0 && (
                      <section>
                        <h4 className="text-body font-semibold text-neutral">Skipped (not a mistake)</h4>
                        <ul className="mt-1 divide-y divide-line rounded-control border border-line px-3" aria-label="Skipped rows">
                          {plan.verdicts.flatMap((v) => (v.kind === 'skip' ? [v] : [])).map((v) => (
                            <li key={v.line} className="py-2 text-body text-neutral"><span className="font-medium">Line {v.line}, {v.name}:</span> {v.reason}</li>
                          ))}
                        </ul>
                      </section>
                    )}

                    {readyRows.length > 0 && (
                      <section>
                        <h4 className="text-body font-semibold text-neutral">Will be added{readyRows.length > PREVIEW_LIMIT ? ` (first ${PREVIEW_LIMIT} shown)` : ''}</h4>
                        <ul className="mt-1 divide-y divide-line rounded-control border border-line px-3" aria-label="People to add">
                          {readyRows.slice(0, PREVIEW_LIMIT).map((r) => (
                            <li key={r.line} className="flex flex-wrap items-baseline justify-between gap-x-4 py-2 text-body">
                              <span className="font-medium text-ink">{r.fullName}</span>
                              <span className="text-muted">
                                {r.phone ? formatZambianPhone(r.phone) : 'No phone'} · joined {formatDay(r.joinedOn)}
                              </span>
                            </li>
                          ))}
                        </ul>
                        {readyRows.length > PREVIEW_LIMIT && <p className="mt-1 text-body text-muted">...and {readyRows.length - PREVIEW_LIMIT} more.</p>}
                        <p className="mt-2 text-body text-muted">Check that the dates look right: dates are read as day/month/year.</p>
                      </section>
                    )}

                    {runError && <p role="alert" className="text-body text-bad">{runError}</p>}
                    {progress ? (
                      <p role="status" className="flex items-center gap-2 text-ink"><Spinner /> Importing {progress.done} of {progress.total}...</p>
                    ) : (
                      <div>
                        <Button size="lg" onClick={() => void run()} disabled={readyRows.length === 0}>
                          {readyRows.length === 0 ? 'Nothing to import' : `Import ${readyRows.length} ${readyRows.length === 1 ? 'person' : 'people'}`}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
